/**
 * ConnectionManagerContext — React provider wrapping the ConnectionManager.
 *
 * Replaces DualSocketProvider. Exports DualSocketContext as an alias
 * for backward compatibility — existing code imports are unchanged.
 */

import React, { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';

import { ActionPathStatus, ConnectionManager, ConnectionStatus } from './ConnectionManager';

type ContextValue = {
  socket: any;
  setGameType: (type: string) => void;
  connectionStatus: ConnectionStatus;
  // Health of the socket the player's ACTIONS travel on, tracked separately from
  // connectionStatus because that one deliberately follows the update socket in
  // dual mode. See ConnectionManager's ActionPathStatus docblock.
  actionPathStatus: ActionPathStatus;
  // Force a full reconnect (fresh tokens, both sockets). Exposed so the player
  // has an in-app recovery path (ConnectionWarning's "Reconnect" button) instead
  // of the only option being kill + relaunch + re-login.
  reconnect: () => void;
  // Re-assert one table's subscription with the engine. The component that
  // submitted a move is the only thing that can tell the move had no effect, and
  // a dropped engine-side registration is the leading cause; this is the cheap
  // repair for it. Idempotent server-side.
  resyncTable: (gameID: string | number) => void;
  /** Mark the table the player is acting on so its feed is judged on a tighter clock. */
  setPriorityTable: (gameID: string | number | null) => void;
  /**
   * Hold the guest-to-member re-handshake until a location verdict exists.
   * Releasing performs any upgrade that arrived while held. Guests are
   * unaffected either way.
   */
  setGeoHold: (hold: boolean, admitted?: boolean) => void;
};

export const ConnectionManagerContext = React.createContext<ContextValue>({
  socket: null,
  setGameType: () => {},
  connectionStatus: 'disconnected',
  actionPathStatus: 'down',
  reconnect: () => {},
  resyncTable: () => {},
  setPriorityTable: () => {},
  setGeoHold: () => {}
});

// Backward compatibility alias
export const DualSocketContext = ConnectionManagerContext;

const ConnectionManagerProvider = ({ children }: PropsWithChildren) => {
  const managerRef = useRef<ConnectionManager | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [actionPathStatus, setActionPathStatus] = useState<ActionPathStatus>('down');
  const [socket, setSocket] = useState<any>(null);
  const initializedRef = useRef(false);

  // Create manager once
  if (!managerRef.current) {
    managerRef.current = new ConnectionManager();
  }

  const manager = managerRef.current;

  // Listen for status changes
  useEffect(() => {
    const unsubscribe = manager.onStatusChange((status) => {
      setConnectionStatus(status);
      // Update the proxy reference when status changes (sockets may have been recreated)
      setSocket(manager.getProxy());
    });

    // Separate subscription: the action path changes independently of the
    // aggregate status (that is the point of it), and it must NOT rebuild the
    // socket proxy — a new proxy identity re-fires every consumer's
    // socket-dependency effects, which is how a subscribe storm starts.
    const unsubscribeActionPath = manager.onActionPathStatusChange(setActionPathStatus);

    return () => {
      unsubscribe();
      unsubscribeActionPath();
      manager.destroy();
    };
  }, [manager]);

  const setGameType = React.useCallback(
    (gameType: string) => {
      if (!initializedRef.current) {
        initializedRef.current = true;
        manager.initialize(gameType);
        setSocket(manager.getProxy());
      }
    },
    [manager]
  );

  const reconnect = React.useCallback(() => {
    manager.reconnect();
  }, [manager]);

  const resyncTable = React.useCallback(
    (gameID: string | number) => {
      manager.resyncTable(gameID);
    },
    [manager]
  );

  const setPriorityTable = React.useCallback(
    (gameID: string | number | null) => {
      manager.setPriorityTable(gameID);
    },
    [manager]
  );

  const setGeoHold = React.useCallback(
    (hold: boolean, admitted?: boolean) => {
      manager.setGeoHold(hold, admitted);
    },
    [manager]
  );

  // PERF: memoized so consumers only re-render when one of these values
  // actually changes, not on every provider re-render. See
  // docs/investigations/20260819-game-client-overheating.md.
  const value = useMemo(
    () => ({
      socket,
      setGameType,
      connectionStatus,
      actionPathStatus,
      reconnect,
      resyncTable,
      setPriorityTable,
      setGeoHold
    }),
    [
      socket,
      setGameType,
      connectionStatus,
      actionPathStatus,
      reconnect,
      resyncTable,
      setPriorityTable,
      setGeoHold
    ]
  );

  return (
    <ConnectionManagerContext.Provider value={value}>{children}</ConnectionManagerContext.Provider>
  );
};

export default ConnectionManagerProvider;
